import {
    FC,
    ReactElement,
    useEffect,
    useState,
} from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { Page, Wrapper } from '../../../components';
import { RegisterCallToAction } from '../../../compositions';
import { ConnectedBlockRenderer, ConnectedLoginForm, ConnectedPageHelmet } from '../../../connectors';
import { emailAddressParameter } from '../../../entities/Login/Login';
import { AppRoute, appRoutes } from '../../../entities/Routing/Routing';
import { useTrans } from '../../../hooks';
import { fetchCustomer } from '../../../redux/@account/customer/customerActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';

import './LoginPage.scss';

interface LoginPageProps {
    className?: string;
}

export const LoginPage: FC<LoginPageProps> = ({
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const navigate = useNavigate();
    const dispatch = useTypedDispatch();
    const [searchParams] = useSearchParams();

    const hasFetched = useTypedSelector(state => state.customerSlice.hasFetched);
    const isAuthenticated = useTypedSelector(state => state.authenticationSlice.isAuthenticated);

    const [emailAddress, setEmailAddress] = useState<string>('');

    useEffect((): void => {
        if (!searchParams) {
            return;
        }

        const emailAddressQueryParam = searchParams.get(emailAddressParameter);

        if (emailAddressQueryParam) {
            setEmailAddress(emailAddressQueryParam);
        }
    }, [searchParams]);

    useEffect((): void => {
        if (hasFetched && isAuthenticated) {
            navigate(trans(appRoutes[AppRoute.accountDashboard].path));
        }
    }, [hasFetched, isAuthenticated]);

    const handleAfterLogin = async (): Promise<void> => {
        await dispatch(fetchCustomer({
            fetchCartAndWishlist: true,
        }));

        navigate(trans(appRoutes[AppRoute.accountDashboard].path));
    };

    return (
        <Page className={`login-page ${className}`}>
            <ConnectedPageHelmet appRoute={AppRoute.login} />

            <Wrapper className="login-page__wrapper">
                <ConnectedLoginForm
                    heading={trans('containers.loginForm.heading')}
                    emailAddress={emailAddress}
                    onLoggedIn={handleAfterLogin}
                    className="login-page__login-form"
                />

                <RegisterCallToAction className="login-page__registration-call-to-action" />
            </Wrapper>

            <ConnectedBlockRenderer
                appRoute={AppRoute.login}
                className="login-page__section"
            />
        </Page>
    );
};
